// Employer switch for Everyday Services (code name `commerce`): lets employees buy
// airtime, data and electricity from their own mobile money. The employer only
// switches it on/off — they never see purchases and the company is never charged.
//
// Rendered only when the tenant holds the `commerce` product (platform staff grant
// it per tenant). PUT/GET /settings require `commerce:settings` (tenant owner); a
// 403 shows the switch read-only with an "only the owner" note.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { getApiErrorMessage } from '@/lib/apiError';
import { commerceApi } from '@/features/everyday-services/api/commerceApi';
import type { CommerceSettings } from '@/features/everyday-services/types';

type LoadState = 'loading' | 'ready' | 'denied' | 'error';

const CommerceSettingsInner: React.FC = () => {
  const { t } = useTranslation('everydayServices');
  const [s, setS] = useState<CommerceSettings | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setState('loading');
    commerceApi.getSettings()
      .then((v) => { setS(v); setState('ready'); })
      .catch((e) => {
        const status = e?.response?.status;
        setState(status === 403 || status === 401 ? 'denied' : 'error');
      });
  };
  useEffect(load, []);

  const toggle = async (value: boolean) => {
    if (!s) return;
    const prev = s;
    setSaving(true);
    setS({ ...s, enabled: value }); // optimistic
    try {
      setS(await commerceApi.updateSettings(value));
      toast.success(value ? t('settings.savedOn') : t('settings.savedOff'));
    } catch (e) {
      setS(prev); // revert
      toast.error(getApiErrorMessage(e, t('settings.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-orange-600" /> {t('settings.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {state === 'loading' ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> …
          </div>
        ) : state === 'error' ? (
          <DataState compact variant="error" message={t('settings.loadError')} onRetry={load} />
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div className="min-w-0 pr-3">
              <p className="text-sm font-medium text-foreground">{t('settings.label')}</p>
              <p className="text-xs text-muted-foreground">{t('settings.description')}</p>
              {state === 'denied' && (
                <p className="mt-1 text-xs text-muted-foreground">{t('settings.ownerOnly')}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saving ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
              {state === 'ready' && (
                <Switch
                  checked={!!s?.enabled}
                  onCheckedChange={toggle}
                  disabled={!s || saving}
                  aria-label={t('settings.label')}
                />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const CommerceSettingsCard: React.FC = () => {
  // Strict: a paid add-on that must default OFF — never fail open for an
  // un-provisioned tenant (hasProduct would).
  const { loaded, hasProductStrict } = useEntitlements();
  if (!loaded || !hasProductStrict('commerce')) return null;
  return <CommerceSettingsInner />;
};

export default CommerceSettingsCard;
